import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCountItems,
  updateCountItems,
  deleteCount,
} from "../../api/inventory/count_items";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

function sortCountItems(items) {
  return [...items].sort((a, b) => {
    if (a.created_at && b.created_at) {
      return new Date(a.created_at) - new Date(b.created_at);
    }
    return (a.id || 0) - (b.id || 0);
  });
}

export const deleteCountAsync = createAsyncThunk(
  "inventory_counts/deleteCountAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteCount(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "delete_inventory_count_item"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_inventory_count_item");
    }
  }
);

export const updateCountItemsAsync = createAsyncThunk(
  "inventory_count_items/updateCountItemsAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCountItems(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "update_inventory_count_items"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_inventory_count_items");
    }
  }
);

export const getCountItemsAsync = createAsyncThunk(
  "inventory_count_items/getCountItemsAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchCountItems(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "list_inventory_count_items"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_inventory_count_items");
    }
  }
);

const InventoryCountItemSlice = createSlice({
  name: "inventory_count_items",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCountItemsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCountItemsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        let items = [];
        if (Array.isArray(action.payload)) {
          items = action.payload;
        } else if (action.payload?.data) {
          items = Array.isArray(action.payload.data) ? action.payload.data : [];
        } else {
          items = [];
        }
        state.data = sortCountItems(items);
      })
      .addCase(getCountItemsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente tellelinjene."
        );
      })
      .addCase(updateCountItemsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateCountItemsAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          let items = [];
          if (Array.isArray(action.payload)) {
            items = action.payload;
          } else if (action.payload?.data && Array.isArray(action.payload.data)) {
            items = action.payload.data;
          }
          draftState.data = sortCountItems(items);
          draftState.isLoading = false;
          draftState.errors = action.payload?.errors || null;
        });
      })
      .addCase(updateCountItemsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere tellelinjene."
        );
      })
      .addCase(deleteCountAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteCountAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (Array.isArray(action.payload)) {
            draftState.data = action.payload;
          } else if (action.payload?.data && Array.isArray(action.payload.data)) {
            draftState.data = action.payload.data;
          } else {
            draftState.data = draftState.data || [];
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteCountAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette tellelinjen."
        );
      });
  },
});

export const { reducer } = InventoryCountItemSlice;

export default InventoryCountItemSlice;
